import * as THREE from 'three';
import { TAU } from '../../core/math';
import { scene } from '../../render/scene';
import { G, HP, M, clamp } from './kit';
/* ============================================================
   BOSS 的死亡編排（移植自 `enemy_sample_ani.html` §12）
   ============================================================

   由 `tools/port-bosses.mjs` 產生，**不要手改**。

   ## 這裡的亂數是視覺亂數，不是對局亂數

   碎塊的初速、自旋、落點全部用瀏覽器的亂數 —— 它們**不進模擬層**，
   沒有任何邏輯讀得到結果，所以不吃 `core/rng` 的種子。
   （判準見 lessons 2026-08-01：看它的結果會不會被別的邏輯讀到。）

   ## `Object3D.attach()` 是整段的樞紐

   炸裂時要把每個骨段從階層裡放出來。用「position 沿階層相加」估世界座標會
   **忽略旋轉** —— 腿張開的 SPINNE、倒下的 TURM 會在炸裂那一幀瞬移。
   `attach()` 換父節點但保留世界變換，而且**必須由深到淺**搬
   （先搬走的子節點，其祖先的 matrixWorld 還沒被動過）。
   headless 測不到這件事（測試用的 THREE 模擬層沒有矩陣運算）。 */

/** 原型在模組層宣告的暫存物件。**每一顆都是共用的**，不要在迴圈裡 new。 */
var _v2 = new THREE.Vector3(), _sv2 = new THREE.Vector3();
var _q2 = new THREE.Quaternion(), _e2 = new THREE.Euler(), _pm = new THREE.Matrix4();

/* ══ §12 BOSS 死亡 ══════════════════════════════════════════════
   小怪的死亡是「塌下去 + 縮小」，0.6 秒結束 —— 那是給一次死二十隻的東西用的。
   BOSS 需要一整段編排。四拍：

     停頓 → 抖動 → 吸氣 → 炸裂 → 落下

   ⚠️ 第一版把「失壓」做成整體傾斜 1.25 秒再扳正，結果是「歪兩秒、突然回正、才爆」。
   兩個錯：太長，而且動了角度。角度一旦動了就必須在炸裂前歸位，那個歸位一定看得出來。
   現在改成 —— **只動位移不動角度**，而且整段壓到 0.53 秒。
   抖動是高頻小位移（±0.05 單位、每秒約 11 次），讀作「內部在連續爆」，
   不是「它站不穩」。

   ⭐ 這裡有一個意外的紅利：解體不需要新的資料結構。
   `mergeBones()` 當初是為了省 draw call 才把每個骨段烘成一顆 mesh，
   而「一顆剛體 mesh」正好就是碎塊要的東西。
   優化的產物跟死亡動畫的需求是同一個結構 —— 放開就好。

   停頓（hitstop）不是幾何效果，是時間效果：把全域 dt 乘上 0.03，
   畫面沒有凍結（還在算），但所有動作停在原地。這是打擊感最便宜也最有效的一招。 */
export var D_SAG = 0.34, D_INH = 0.52, D_END = 5.60;
export var Death: any = {
  on:0, t:0, burst:0, stop:0, chunks:null, mats:null, cy:2,
  fx:null, jit:0, pop:0,
  ensureFX:function(){
    if (this.fx) return;
    var mk = function(hex, op){
      return new THREE.MeshBasicMaterial({color:hex, transparent:true, opacity:op,
        depthWrite:false, blending:THREE.AdditiveBlending});
    };
    var g = new THREE.Group(); scene.add(g);
    /* 通用碎片：一顆 InstancedMesh 28 塊 = 1 draw call。
       骨段是「認得出來的部件」，這些是「認不出來的東西」，兩種都要有才像爆炸。 */
    var sh = new THREE.InstancedMesh(G.box, M.dark, 28);
    sh.frustumCulled = false; sh.count = 0; sh.castShadow = false; g.add(sh);
    this.shards = sh; this.sdat = [];
    var mf = mk(0xffe0b0, 1), mr = mk(0xffb060, 1), md = mk(0x9a6a3a, 1);
    var flash = new THREE.Mesh(G.oct, mf); flash.renderOrder = 4; g.add(flash);
    var rings = [], i;
    for (i = 0; i < 3; i++){
      var r = new THREE.Mesh(G.tor, i === 2 ? md : mr);
      r.rotation.x = HP; r.renderOrder = 4; g.add(r); rings.push(r);
    }
    var pops = [];
    for (i = 0; i < 5; i++){
      var pm = new THREE.Mesh(G.oct, mk(0xffcf90, 1)); pm.renderOrder = 4; g.add(pm);
      pops.push({m:pm, t:9, x:0, y:0, z:0});
    }
    g.visible = false;
    this.fx = {g:g, flash:flash, rings:rings, pops:pops, mf:mf, mr:mr, md:md};
  },
  /* 開始死亡：收集所有會發光的材質（等一下要抽乾），記住 BOSS 的中心高度 */
  arm:function(root, T){
    this.ensureFX();
    this.on = 1; this.t = 0; this.burst = 0; this.stop = 0.20;
    this.chunks = null; this.jit = 0; this.pop = 0;
    this.cy = T.h * 0.45;
    this.ox = 0; this.oy = this.cy; this.oz = 0;
    this.rad = Math.max(T.h, T.w) * 0.30; this.fxr = clamp(this.rad, 1.2, 6.0);
    /* 抖動幅度依體型 —— TURM（14 高）跟 WALZE（3.3 高）不該抖一樣多 */
    this.amp = clamp(T.h * 0.020, 0.05, 0.26);
    root.userData.px = root.position.x;
    root.userData.pz = root.position.z;
    var ms = [];
    root.traverse(function(c){
      if (c.material && c.material.emissive && ms.indexOf(c.material) < 0) ms.push(c.material);
    });
    this.mats = ms;
    this.fx.g.visible = true;
    this.fx.g.position.set(0, 0, 0);
    for (var i = 0; i < 5; i++) this.fx.pops[i].t = 9;
  },
  /* 炸裂：把每個骨段從階層裡放出來，攤平成 root 底下的自由剛體。

     ⚠️ 第一版用「把 position 沿著階層相加」估世界座標，**忽略了旋轉**。
     結果是骨段一放開就跳到錯的位置與錯的角度 —— 腿張開的 SPINNE、
     倒下的 TURM、環翻開的 GERIPPE 全部會在炸裂那一幀瞬移。
     正解是 `Object3D.attach()`：換父節點但保留世界變換。
     碎塊從**牠死的那一刻真正所在的位置與角度**開始飛，完全不跳。

     要由深到淺處理 —— 先搬走的子節點，其祖先的 matrixWorld 還沒被動過。 */
  release:function(root){
    var list = [], self = this;
    /* 要穿過「不是骨段的容器」—— 有些 BOSS 把腿掛在一個普通 Group 底下，
       只認 bone 的話那幾條腿會整組留在原地不炸。 */
    (function walk(node, ax, ay, az, inBone){
      for (var i = 0; i < node.children.length; i++){
        var c = node.children[i];
        if (c === self.shards) continue;
        var wx = ax + c.position.x, wy = ay + c.position.y, wz = az + c.position.z;
        var isBone = !!(c.userData && c.userData.bone);
        if (!c.isMesh || isBone) walk(c, wx, wy, wz, inBone || isBone);
        if (isBone || (c.isMesh && !inBone)) list.push({o:c, wx:wx, wy:wy, wz:wz});
      }
    })(root, 0, 0, 0, false);
    var out = [], i, o, it;
    /* 第一趟：搬。attach 保留世界變換，所以姿勢是連續的。 */
    for (i = 0; i < list.length; i++){              // list 已是由深到淺
      it = list[i]; o = it.o;
      if (root.attach) root.attach(o);              // ← 真正的解法
      else { root.add(o); o.position.set(it.wx, it.wy, it.wz); }  // 無 attach 時的退路
    }
    /* 第二趟：用搬完之後的真實座標算爆心與初速。
       爆心＝所有碎塊的形心，不是固定的 T.h*0.45 ——
       倒下的 TURM 與立著的 TURM 應該從不同的地方炸開。 */
    var ox = 0, oy = 0, oz = 0;
    for (i = 0; i < list.length; i++){
      o = list[i].o; ox += o.position.x; oy += o.position.y; oz += o.position.z;
    }
    var n = Math.max(1, list.length);
    ox /= n; oy /= n; oz /= n;
    this.ox = ox; this.oy = oy; this.oz = oz;
    var rad = 0.5;
    for (i = 0; i < list.length; i++){
      o = list[i].o;
      rad = Math.max(rad, Math.sqrt((o.position.x-ox)*(o.position.x-ox)
                                  + (o.position.y-oy)*(o.position.y-oy)
                                  + (o.position.z-oz)*(o.position.z-oz)));
    }
    this.rad = rad;
    this.fxr = clamp(rad, 1.2, 6.0);   // FX 尺寸另外夾住，22 單位長的蛇不該閃出 40 單位的光
    for (i = 0; i < list.length; i++){
      o = list[i].o;
      var dx = o.position.x - ox, dy = o.position.y - oy, dz = o.position.z - oz;
      var d = Math.sqrt(dx*dx + dy*dy + dz*dz) + 0.001;
      var sp = 2.6 + 5.8*Math.random();   // det-ok: 碎塊只在渲染層，模擬層讀不到
      out.push({o:o,
        vx: dx/d*sp*1.20 + (Math.random()-0.5)*1.6,   // det-ok: 碎塊只在渲染層，模擬層讀不到
        vy: dy/d*sp*0.70 + 3.6 + 2.6*Math.random(),   // det-ok: 碎塊只在渲染層，模擬層讀不到
        vz: dz/d*sp*1.20 + (Math.random()-0.5)*1.6,   // det-ok: 碎塊只在渲染層，模擬層讀不到
        sx:(Math.random()-0.5)*11, sy:(Math.random()-0.5)*11, sz:(Math.random()-0.5)*11,   // det-ok: 碎塊只在渲染層，模擬層讀不到
        rest: 0.10 + Math.random()*0.30, dead:0});   // det-ok: 碎塊只在渲染層，模擬層讀不到
    }
    this.chunks = out;
    /* 同時撒一批通用碎片，大小依 BOSS 體型 */
    var sz = Math.max(0.22, this.rad * 0.20);
    this.sdat = [];
    for (i = 0; i < 28; i++){
      var a = Math.random()*TAU, e = (Math.random()-0.3)*1.5, sp2 = 4.5 + 9*Math.random();   // det-ok: 碎塊只在渲染層，模擬層讀不到
      this.sdat.push({
        x:this.ox + (Math.random()-0.5)*this.rad,   // det-ok: 碎塊只在渲染層，模擬層讀不到
        y:this.oy + (Math.random()-0.5)*this.rad,   // det-ok: 碎塊只在渲染層，模擬層讀不到
        z:this.oz + (Math.random()-0.5)*this.rad,   // det-ok: 碎塊只在渲染層，模擬層讀不到
        vx:Math.sin(a)*Math.cos(e)*sp2, vy:Math.sin(e)*sp2 + 5.5*Math.random(),   // det-ok: 碎塊只在渲染層，模擬層讀不到
        vz:Math.cos(a)*Math.cos(e)*sp2,
        rx:Math.random()*TAU, ry:Math.random()*TAU, rz:Math.random()*TAU,   // det-ok: 碎塊只在渲染層，模擬層讀不到
        sx:(Math.random()-0.5)*15, sy:(Math.random()-0.5)*15, sz:(Math.random()-0.5)*15,   // det-ok: 碎塊只在渲染層，模擬層讀不到
        k: sz*(0.35 + Math.random()), rest:0.06 + Math.random()*0.16, dead:0});   // det-ok: 碎塊只在渲染層，模擬層讀不到
    }
    this.shards.count = 28;
  },
  stepShards:function(rdt){
    var i, d, n = this.sdat.length;
    for (i = 0; i < n; i++){
      d = this.sdat[i];
      if (!d.dead){
        d.vy -= 17*rdt;
        d.x += d.vx*rdt; d.y += d.vy*rdt; d.z += d.vz*rdt;
        d.rx += d.sx*rdt; d.ry += d.sy*rdt; d.rz += d.sz*rdt;
        if (d.y < d.rest){
          d.y = d.rest; d.vy = -d.vy*0.24; d.vx *= 0.5; d.vz *= 0.5;
          d.sx *= 0.3; d.sy *= 0.3; d.sz *= 0.3;
          if (Math.abs(d.vy) < 0.5){ d.vy = 0; d.dead = 1; }
        }
      }
      _e2.set(d.rx, d.ry, d.rz); _q2.setFromEuler(_e2);
      _pm.compose(_v2.set(d.x, d.y, d.z), _q2, _sv2.set(d.k, d.k*0.62, d.k*1.3));
      this.shards.setMatrixAt(i, _pm);
    }
    this.shards.instanceMatrix.needsUpdate = true;
  },
  /* 回傳這一幀給場景用的 timeScale */
  step:function(rdt, root){
    if (!this.on) return 1;
    var F = this.fx, i, c;
    if (this.stop > 0){ this.stop -= rdt; this.flashStep(rdt); return 0.03; }
    this.t += rdt;
    var t = this.t, sc = 1;

    if (!this.burst && t >= D_INH){                 // ── 炸裂 ──
      this.burst = 1; this.stop = 0.15;
      /* 先把抖動位移與吸氣縮放歸位，再 attach —— 否則那 0.05 的偏移會被烘進碎塊 */
      root.position.set(root.userData.px || 0, 0, root.userData.pz || 0);
      root.scale.setScalar(root.userData.sc0 || 1);
      if (root.updateMatrixWorld) root.updateMatrixWorld(true);
      this.release(root);
      /* FX 跟著 root 的位置與朝向走，這樣爆心的 local 座標才對得上 */
      F.g.position.set(root.position.x, 0, root.position.z);
      F.g.rotation.y = root.rotation.y;
      F.g.scale.setScalar(root.scale.x || 1);
      F.flash.position.set(this.ox, this.oy, this.oz); F.flash.scale.setScalar(0.4*this.fxr);
      F.mf.opacity = 1;
      for (i = 0; i < 3; i++){ F.rings[i].scale.set(0.6,0.6,0.10);
        F.rings[i].position.set(this.ox, 0.10 + i*0.05, this.oz); }
      F.mr.opacity = 1; F.md.opacity = 0.8;
    }

    if (!this.burst){
      var sag = clamp(t / D_SAG, 0, 1);
      var inh = clamp((t - D_SAG) / (D_INH - D_SAG), 0, 1);
      sc = 0.55 + 0.40*sag;                          // 只微微放慢，讓抖動還讀得出頻率
      if (inh > 0) sc = 0.50;
      /* 二次爆：0.34 秒內打三發 */
      this.jit += rdt;
      if (this.jit > 0.11){
        this.jit = 0;
        var pk = F.pops[this.pop % 5]; this.pop++;
        pk.t = 0;
        pk.x = (Math.random()-0.5)*this.rad*1.5;   // det-ok: 碎塊只在渲染層，模擬層讀不到
        pk.y = this.cy + (Math.random()-0.5)*this.rad*1.4;   // det-ok: 碎塊只在渲染層，模擬層讀不到
        pk.z = (Math.random()-0.5)*this.rad*1.5;   // det-ok: 碎塊只在渲染層，模擬層讀不到
      }
      /* ⭐ 只動位移，不動角度。高頻小抖 + 吸氣時一點點下沉。 */
      var amp = this.amp * (0.4 + sag);
      root.position.x = (root.userData.px || 0) + Math.sin(t*71)*amp;
      root.position.z = (root.userData.pz || 0) + Math.sin(t*63 + 1.7)*amp;
      root.position.y = Math.sin(t*88)*amp*0.6 - 0.08*inh;
      root.scale.setScalar((root.userData.sc0 || 1) * (1 - 0.045*inh));
      /* 發光：抽乾 → 吸氣時燒白 */
      var k = (1 - 0.78*sag) + 6.0*inh*inh;
      for (i = 0; i < this.mats.length; i++)
        this.mats[i].emissiveIntensity *= k;
    } else {
      /* ── 落下：每塊都是自由剛體 ── */
      for (i = 0; i < this.chunks.length; i++){
        c = this.chunks[i];
        if (c.dead) continue;
        c.vy -= 16.5*rdt;
        c.o.position.x += c.vx*rdt;
        c.o.position.y += c.vy*rdt;
        c.o.position.z += c.vz*rdt;
        c.o.rotation.x += c.sx*rdt; c.o.rotation.y += c.sy*rdt; c.o.rotation.z += c.sz*rdt;
        if (c.o.position.y < c.rest){
          c.o.position.y = c.rest;
          c.vy = -c.vy*0.26; c.vx *= 0.58; c.vz *= 0.58;
          c.sx *= 0.34; c.sy *= 0.34; c.sz *= 0.34;
          if (Math.abs(c.vy) < 0.55){ c.vy = 0; c.dead = 1; }
        }
      }
      this.stepShards(rdt);
      for (i = 0; i < this.mats.length; i++) this.mats[i].emissiveIntensity *= 0.02;
      var ft = clamp((t - D_INH) / 0.55, 0, 1);
      F.flash.scale.setScalar(this.fxr*(0.4 + 3.4*ft));
      F.mf.opacity = (1 - ft)*(1 - ft);
      for (i = 0; i < 3; i++){
        var rt = clamp((t - D_INH - i*0.09) / (0.85 + i*0.35), 0, 1);
        var rr = this.fxr*(0.5 + 7.0*rt*(1 - 0.35*rt));
        F.rings[i].scale.set(rr, rr, 0.09);
        F.rings[i].visible = rt > 0 && rt < 1;
      }
      F.mr.opacity = Math.max(0, 1 - (t - D_INH)/1.0);
      F.md.opacity = Math.max(0, 0.8 - (t - D_INH)/1.8);
    }
    this.flashStep(rdt);
    return sc;
  },
  flashStep:function(rdt){
    var F = this.fx, i, pk;
    for (i = 0; i < 5; i++){
      pk = F.pops[i];
      if (pk.t > 1.2){ pk.m.visible = false; continue; }
      pk.t += rdt; pk.m.visible = true;
      pk.m.position.set(pk.x, pk.y, pk.z);
      pk.m.scale.setScalar((0.3 + 4.2*pk.t) * clamp(this.fxr*0.22, 0.5, 2.2));
      pk.m.material.opacity = Math.max(0, 1 - pk.t/0.55);
    }
  },
  reset:function(){
    this.on = 0; this.burst = 0; this.chunks = null; this.stop = 0;
    if (this.shards) this.shards.count = 0;
    if (this.fx) this.fx.g.visible = false;
  }
};
